import { gridSize } from '@/shared';
import { Theme } from '@emotion/react';
import { Autocomplete, Grid, SxProps, TextField } from '@mui/material';
import { Control, Controller, FieldError } from 'react-hook-form';
import './CustomAutocomplete.css';
import { useEffect } from 'react';

export type CustomAutocompleteOptimizedProps<T> = {
  name: string;
  loadingText?: string;
  label: string;
  disabled?: boolean;

  options: T[];
  valueKey: keyof T;
  actualValueKey?: keyof T;
  optionLabelForEdit?: string;
  isLoadingData: boolean;
  onChangeValue?: (value: any) => void;
  onChangeRawValue?: (value: T) => void;
  defaultValue?: string | number | null | undefined;

  error: FieldError | undefined;
  helperText: React.ReactNode;
  required?: boolean;

  textFieldKey?: string;
  control: Control<any, any>;

  size?: {
    xs: number;
    sm: number;
    md: number;
    lg: number;
  };
  disableClearable?: boolean;

  sxTextField?: SxProps<Theme>;
  endAdornmentInput?: React.ReactNode;

  //send with enter
  enableToSubmitWithEnter?: boolean;
  handleSubmit?: () => void;
  isOptionEqualToValue?: any;
  infoText?: string;
};

function CustomAutocompleteOptimized<T>({
  name,
  options,
  isLoadingData,
  error,
  textFieldKey,
  defaultValue,
  control,
  loadingText = 'Cargando...',
  valueKey,
  helperText,
  required = true,
  label,
  actualValueKey,
  onChangeValue,
  onChangeRawValue,
  size = gridSize,
  disabled = false,
  disableClearable = false,
  endAdornmentInput,
  enableToSubmitWithEnter = false,
  handleSubmit = () => console.log("nothing happen"),
  isOptionEqualToValue,
  infoText,
}: Readonly<CustomAutocompleteOptimizedProps<T>>) {

  return (
    <Grid item {...size}>
      <Controller
        name={name}
        control={control}
        key={`${textFieldKey ?? name}-${options.length}`}
        defaultValue={defaultValue ?? ''}
        render={({ field }) => {

          const onKeyDown = (_event: any) => {
            if (enableToSubmitWithEnter && _event.key === 'Enter') {
              handleSubmit();
            }
          };

          // Calcular el valor seleccionado FUERA del render del Autocomplete
          const selectedValue = options.find(
            opt => opt?.[actualValueKey ?? valueKey] === field.value
          ) || null;


          useEffect(() => {
            if (!isLoadingData && options.length > 0 && field.value && !selectedValue) {
              // Las opciones ya cargaron pero no se encontró el valor
              // Intentar encontrarlo nuevamente
              const found = options.find(
                opt => opt?.[actualValueKey ?? valueKey] === field.value
              );
              if (found) {
                // Forzar actualización
                field.onChange(field.value);
              }
            }
          }, [isLoadingData, options, field.value]);
          const displayHelperText = error ? helperText : (infoText || '');

          return (
            <Autocomplete
              popupIcon={null}
              disabled={disabled}
              noOptionsText="No hay opciones"
              loading={isLoadingData}
              loadingText={loadingText}
              options={options}
              disableClearable={disableClearable}
              value={selectedValue} // ✅ Valor directo, no función
              isOptionEqualToValue={isOptionEqualToValue ?? ((opt: any, val: any) => {
                if (!opt || !val) return false;
                return opt?.[actualValueKey ?? valueKey] === val?.[actualValueKey ?? valueKey];
              })}
              getOptionLabel={(option: any) => {
                if (!option) return '';
                
                // Si option es un objeto, obtener el label
                if (typeof option === 'object') {
                  return option?.[valueKey]?.toString() ?? '';
                }
                
                // Si option es un valor primitivo (string/number), buscar en options
                const foundOption = options.find(
                  opt => opt[actualValueKey ?? valueKey] === option
                );
                return foundOption?.[valueKey]?.toString() ?? '';
              }}
              onChange={(_event, data: any) => {
                const selectedValue = data?.[actualValueKey ?? valueKey] ?? '';
                field.onChange(selectedValue);
                onChangeValue?.(selectedValue);
                onChangeRawValue?.(data);
              }}
              onKeyDown={onKeyDown}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={label}
                  error={!!error}
                  helperText={displayHelperText}
                  required={required}
                  variant="outlined"
                  autoComplete="off"
                  InputProps={{
                    ...params.InputProps,
                    ...(endAdornmentInput && { endAdornment: endAdornmentInput }),
                  }}
                  sx={{
                    '& .css-1i3ux3q-MuiInputBase-input-MuiOutlinedInput-input.Mui-disabled': {
                      opacity: 0.8,
                      WebkitTextFillColor: 'black',
                    },
                    "& .MuiOutlinedInput-root": {
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#d3d3d3",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#9395ec", 
                      },
                    },
                  }}
                />
              )}
            />
          );
        }}
      />
    </Grid>
  );
}

export default CustomAutocompleteOptimized;